import { css } from "@emotion/react";
import { type PropsWithChildren, useContext, useEffect, useMemo, useRef } from "react";
import { ContextMenu } from "reshaped";

import { getActionAnchorProps, getActionClickableProps } from "../../action/Action";
import { iconsComponents } from "../Icon/constants";
import { type ContextualMenuLayer, ContextualMenuStackContext } from "./context";
import type { ContextualMenuItem } from "./types";

type WithContextualMenuProps = PropsWithChildren<{
    label: string;
    items: ContextualMenuItem[];
}>;

export default function WithContextualMenu(props: WithContextualMenuProps) {
    const { label, items, children } = props;
    const ancestors = useContext(ContextualMenuStackContext);
    const stack = useMemo(
        () => (items.length === 0 ? ancestors : [...ancestors, { label, items }]),
        [ancestors, items, label],
    );

    if (items.length === 0) {
        return children;
    }

    const layers = [...stack].reverse();
    const singleLayer = layers.length === 1 ? layers[0] : null;

    return (
        <ContextualMenuStackContext.Provider value={stack}>
            <StopNativeContextMenuPropagation>
                <ContextMenu>
                    {children}
                    <ContextMenu.Content>
                        {singleLayer ? (
                            <LayerItems items={singleLayer.items} />
                        ) : (
                            layers.map((layer, index) => (
                                <SubMenuLayer key={`${layer.label}-${index}`} layer={layer} />
                            ))
                        )}
                    </ContextMenu.Content>
                </ContextMenu>
            </StopNativeContextMenuPropagation>
        </ContextualMenuStackContext.Provider>
    );
}

function StopNativeContextMenuPropagation(props: PropsWithChildren) {
    const { children } = props;
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const stop = (event: Event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        el.addEventListener("contextmenu", stop);
        return () => el.removeEventListener("contextmenu", stop);
    }, []);
    return (
        <div ref={ref} css={css({ display: "contents" })}>
            {children}
        </div>
    );
}

function SubMenuLayer(props: { layer: ContextualMenuLayer }) {
    const { layer } = props;
    return (
        <ContextMenu.SubMenu>
            <ContextMenu.SubTrigger>{layer.label}</ContextMenu.SubTrigger>
            <ContextMenu.Content>
                <LayerItems items={layer.items} />
            </ContextMenu.Content>
        </ContextMenu.SubMenu>
    );
}

function LayerItems(props: { items: ContextualMenuItem[] }) {
    const { items } = props;
    return items.map((item, index) => (
        <ContextMenu.Item
            key={`${item.label}-${index}`}
            {...getActionAnchorProps(item.action)}
            {...getActionClickableProps(item.action)}
            {...(item.icon ? { icon: iconsComponents[item.icon] } : {})}
            {...(item.dangerous ? { color: "critical" as const } : {})}
        >
            {item.label}
        </ContextMenu.Item>
    ));
}
